/**
 * SettingsCache — module-level singleton that stores getSettings responses keyed by
 * SettingsCategory.  Populated by useCategoryVisibility (which fetches every category
 * on app start) and consumed by useSettingsPage so that navigating to a category never
 * triggers a redundant bridge call.
 *
 * Invalidation:
 *  - Per-category: called after setSetting / restoreDefaults so the next load reflects
 *    the latest native state.
 *  - Full: utility helper for testing / hard refresh scenarios.
 *
 * TTL: entries older than TTL_MS are treated as misses and re-fetched from the bridge.
 */

/** Shape of a single entry returned by getSettings */
export interface SettingEntry {
	settingKey: string;
	settingValue: string;
	enabled: boolean;
	hidden: boolean;
}

interface CacheEntry {
	settings: SettingEntry[];
	fetchedAt: number;
}

const TTL_MS = 5 * 60 * 1000; // 5 minutes

export class SettingsCache {
	private store = new Map<string, CacheEntry>();

	/**
	 * Returns cached settings for the given category, or null on miss/expiry.
	 */
	get(category: string): SettingEntry[] | null {
		const entry = this.store.get(category);
		if (!entry) return null;
		if (Date.now() - entry.fetchedAt > TTL_MS) {
			this.store.delete(category);
			return null;
		}
		return entry.settings;
	}

	/**
	 * Stores (or overwrites) the settings array for the given category.
	 */
	set(category: string, settings: SettingEntry[]): void {
		this.store.set(category, { settings, fetchedAt: Date.now() });
	}

	/**
	 * Removes the cached data for a single category — call after a successful write
	 * so the next read fetches fresh state from the bridge.
	 */
	invalidate(category: string): void {
		this.store.delete(category);
	}

	/** Clears all cached entries. */
	invalidateAll(): void {
		this.store.clear();
	}

	/** Returns true if a valid (non-expired) entry exists for the category. */
	has(category: string): boolean {
		return this.get(category) !== null;
	}
}

/** Shared singleton instance used across the Settings module. */
export const settingsCache = new SettingsCache();
